import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import { cartoesApi, transacoesApi } from '../../api/endpoints'
import { Modal } from '../../components/ui/Modal'
import { ProgressBar } from '../../components/ui/Card'
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/EmptyState'
import { formatMoney, formatDate } from '../../lib/utils'
import type { FaturaCategoriaLinha } from '../../types'

const CHART_COLORS = ['#FFB300', '#22C55E', '#3B82F6', '#EF4444', '#A78BFA', '#F97316', '#EC4899', '#14B8A6', '#84CC16', '#F59E0B']
const CHART_TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }

/**
 * Gasto por categoria de uma fatura (compras), mais as linhas "Saldo do mês anterior" e "Ajustes e pagamentos",
 * que fecham com o total da fatura. Tocar numa categoria abre as compras dela.
 * Usado na tela de Cartões (por fatura) e na aba "Por fatura" de Relatórios.
 */
export function FaturaCategoriasPanel({ faturaId }: { faturaId: string }) {
  const [aberta, setAberta] = useState<FaturaCategoriaLinha | null>(null)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['fatura-categorias', faturaId],
    queryFn: () => cartoesApi.categoriasDaFatura(faturaId),
  })

  if (isLoading) return <LoadingState />
  if (isError || !data) return <ErrorState message={(error as Error | null)?.message ?? 'Não foi possível carregar as categorias da fatura.'} />

  const { categorias, ajustes, saldoAnterior, encargos, totalCompras, fatura } = data
  const cor = (c: FaturaCategoriaLinha, i: number) => c.cor ?? CHART_COLORS[i % CHART_COLORS.length]
  const temSaldoAnterior = !!saldoAnterior && (saldoAnterior.total !== 0 || saldoAnterior.quantidade > 0)
  const temAjustes = !!ajustes && (ajustes.total !== 0 || ajustes.quantidade > 0)

  return (
    <div className="space-y-4">
      {categorias.length === 0 ? (
        <EmptyState icon="ri-pie-chart-line" message="Nenhuma compra nesta fatura." />
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="mx-auto h-[180px] w-[180px] flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categorias} dataKey="total" nameKey="nome" innerRadius={52} outerRadius={80} strokeWidth={0} paddingAngle={2}>
                  {categorias.map((c, i) => <Cell key={c.categoriaId ?? 'sem'} fill={cor(c, i)} />)}
                </Pie>
                <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="flex-1 space-y-1">
            {categorias.map((c, i) => (
              <li key={c.categoriaId ?? 'sem'}>
                <button
                  type="button" onClick={() => setAberta(c)}
                  className="block w-full rounded px-2 py-1.5 text-left transition-colors hover:bg-surface2/60"
                  title="Ver as compras desta categoria"
                >
                  <div className="mb-1 flex items-center justify-between gap-2 text-13">
                    <span className="flex min-w-0 items-center gap-2 text-white">
                      <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: cor(c, i) }} />
                      <span className="truncate">{c.icone} {c.nome}</span>
                      <span className="text-2xs text-muted">{c.quantidade} {c.quantidade === 1 ? 'compra' : 'compras'}</span>
                    </span>
                    <span className="whitespace-nowrap font-medium text-white">
                      {formatMoney(c.total)} <span className="text-muted">· {c.percentual.toFixed(0)}%</span>
                    </span>
                  </div>
                  <ProgressBar value={c.percentual} tone="muted" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <dl className="space-y-1 rounded-lg border border-line p-3 text-13">
        <div className="flex justify-between gap-2"><dt className="text-muted">Compras</dt><dd className="font-medium text-white">{formatMoney(totalCompras)}</dd></div>
        {temSaldoAnterior && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted" title="Dívida que veio de antes: não é gasto novo.">Saldo do mês anterior</dt>
            <dd className="font-medium text-white">{formatMoney(saldoAnterior.total)}</dd>
          </div>
        )}
        {temAjustes && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted" title="Estornos e pagamentos recebidos.">Ajustes e pagamentos</dt>
            <dd className="font-medium text-income">{formatMoney(ajustes.total)}</dd>
          </div>
        )}
        {encargos && encargos.total !== 0 && (
          <div className="flex justify-between gap-2"><dt className="text-muted">Encargos</dt><dd className="font-medium text-white">{formatMoney(encargos.total)}</dd></div>
        )}
        <div className="flex justify-between gap-2 border-t border-line pt-1">
          <dt className="font-medium text-white">Total da fatura</dt>
          <dd className="font-semibold text-white">{formatMoney(fatura.total)}</dd>
        </div>
      </dl>

      <ComprasDaCategoria faturaId={faturaId} categoria={aberta} temSaldoAnterior={temSaldoAnterior} onClose={() => setAberta(null)} />
    </div>
  )
}

function ComprasDaCategoria({ faturaId, categoria, temSaldoAnterior, onClose }: { faturaId: string; categoria: FaturaCategoriaLinha | null; temSaldoAnterior: boolean; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['fatura-categoria-compras', faturaId, categoria?.categoriaId ?? 'sem'],
    queryFn: () => transacoesApi.list({
      faturaId, apenasCompras: true,
      categoriaId: categoria!.categoriaId ?? undefined,
      semCategoria: categoria!.categoriaId ? undefined : true,
      itensPorPagina: 100, ordenarPor: 'valor', ordemDesc: true,
    }),
    enabled: !!categoria,
  })
  const itens = data?.itens ?? []

  return (
    <Modal open={!!categoria} onClose={onClose} title={categoria ? `${categoria.icone ?? ''} ${categoria.nome}`.trim() : ''}>
      {isLoading ? <LoadingState /> : isError ? <ErrorState message="Erro ao carregar as compras." /> : itens.length === 0 ? (
        <EmptyState icon="ri-shopping-bag-3-line" message="Nenhuma compra encontrada." />
      ) : (
        <>
        {categoria && !categoria.categoriaId && temSaldoAnterior && (
          <p className="mb-2 text-2xs text-muted">Aqui pode aparecer também a linha do saldo do mês anterior, que fica separada no resumo da fatura.</p>
        )}
        <ul className="divide-y divide-border/60">
          {itens.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 py-2 text-13">
              <span className="min-w-0">
                <span className="block truncate font-medium text-white">
                  {t.description}{t.parcelaNumero ? ` · ${t.parcelaNumero}/${t.parcelaTotalNumero}` : ''}
                </span>
                <span className="text-2xs text-muted">{formatDate(t.date)}</span>
              </span>
              <span className="whitespace-nowrap font-semibold text-white">{formatMoney(Math.abs(t.amount))}</span>
            </li>
          ))}
        </ul>
        </>
      )}
    </Modal>
  )
}
